import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  TablePagination,
  useTheme,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Block as BlockIcon,
  Mail as MailIcon,
} from "@mui/icons-material";
import { APP_TITLE } from "../../constant";
import InviteUserModal from "../../modules/UserManagement/InviteUserModal ";

const users = [
  {
    name: "John Anderson",
    email: "john.anderson@company.com",
    role: "HR Admin",
    status: "Active",
    lastLogin: "December 24, 2024",
    action: "Disable",
  },
  {
    name: "Lisa Martinez",
    email: "lisa.martinez@company.com",
    role: "HR User",
    status: "Active",
    lastLogin: "December 23, 2024",
    action: "Disable",
  },
  {
    name: "David Thompson",
    email: "david.thompson@company.com",
    role: "HR User",
    status: "Active",
    lastLogin: "December 22, 2024",
    action: "Disable",
  },
  {
    name: "Maria Garcia",
    email: "maria.garcia@company.com",
    role: "HR Admin",
    status: "Invited",
    lastLogin: "—",
    action: "Resend Invite",
  },
  {
    name: "Robert Chen",
    email: "robert.chen@company.com",
    role: "HR User",
    status: "Disabled",
    lastLogin: "December 15, 2024",
    action: "Enable",
  },
];

const UserManagement = () => {
  const theme = useTheme();
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusIcon = (status) => {
    const iconProps = { sx: { fontSize: 18 } };
    switch (status) {
      case "Active":
        return (
          <CheckCircleIcon
            {...iconProps}
            sx={{ ...iconProps.sx, color: "#10b981" }}
          />
        );
      case "Invited":
        return (
          <AccessTimeIcon
            {...iconProps}
            sx={{ ...iconProps.sx, color: "#f59e0b" }}
          />
        );
      case "Disabled":
        return (
          <BlockIcon
            {...iconProps}
            sx={{ ...iconProps.sx, color: "#6b7280" }}
          />
        );
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#d1fae5", color: "#065f46" };
      case "Invited":
        return { backgroundColor: "#fef3c7", color: "#92400e" };
      case "Disabled":
        return {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(107, 114, 128, 0.2)"
              : "#f3f4f6",
          color: theme.palette.mode === "dark" ? "#9ca3af" : "#374151",
        };
      default:
        return {};
    }
  };

  const getActionColor = (action) => {
    if (action === "Enable") return "success";
    if (action === "Resend Invite") return "primary";
    return "error";
  };

  return (
    <>
      <Helmet>
        <title>User Management | {APP_TITLE}</title>
        <meta
          name="User Management"
          content="Manage portal access via Microsoft 365 SSO (invite-only)"
        />
      </Helmet>

      <Box
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "flex-start" },
            gap: { xs: 2, sm: 0 },
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              User Management
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Manage portal access via Microsoft 365 SSO (invite-only)
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenInviteModal(true)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              textTransform: "none",
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
              alignSelf: { xs: "flex-start", sm: "center" },
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Invite User
          </Button>
        </Box>

        {/* Info Alert */}
        <Alert
          icon={<MailIcon sx={{ color: theme.palette.primary.main }} />}
          sx={{
            mb: 3,
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(59, 130, 246, 0.1)"
                : "#eff6ff",
            color:
              theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : "#1e40af",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(59, 130, 246, 0.3)"
                : "#bfdbfe"
            }`,
            "& .MuiAlert-icon": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Microsoft 365 SSO Only
          </Typography>
          <Typography variant="body2">
            All users must authenticate using their work Microsoft 365 account.
            Users will receive an invitation email and can only access the
            platform after accepting the invite.
          </Typography>
        </Alert>

        {/* Users Table */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Portal Users
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "#f9fafb",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    Work Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    Last Login
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((user, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "#f9fafb",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {user.name}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        size="small"
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(168, 85, 247, 0.2)"
                              : "#f3e8ff",
                          color:
                            theme.palette.mode === "dark"
                              ? "#c084fc"
                              : "#6b21a8",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {getStatusIcon(user.status)}
                        <Chip
                          label={user.status}
                          size="small"
                          sx={{
                            ...getStatusColor(user.status),
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                      {user.lastLogin}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color={getActionColor(user.action)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.875rem",
                        }}
                      >
                        {user.action}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={users.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              color: theme.palette.text.primary,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Paper>
      </Box>

      {/* Invite User Modal */}
      <InviteUserModal
        open={openInviteModal}
        onClose={() => setOpenInviteModal(false)}
      />
    </>
  );
};

export default UserManagement;
